import { Link, useParams } from "react-router-dom";
import { ArrowRight, ArrowLeft, Check } from "lucide-react";
import { SiteHeader } from "../../components/site/SiteHeader";
import { SiteFooter } from "../../components/site/SiteFooter";
import { PageHero } from "../../components/site/PageHero";
import { CTABand } from "../../components/site/CTABand";
import { Reveal, Stagger, StaggerItem } from "../../components/motion/Reveal";
import { PayNow } from "../../components/site/PayNow";
import { getService, SERVICES } from "../../data/services";
import NotFound from "./NotFound";

export default function ServiceDetail() {
  const { slug } = useParams();
  const service = getService(slug);
  if (!service) return <NotFound />;

  const idx = SERVICES.findIndex((s) => s.slug === slug);
  const next = SERVICES[(idx + 1) % SERVICES.length];

  return (
    <div className="font-publicsans bg-nx-navy text-slate-200 antialiased" data-testid={`service-detail-${slug}`}>
      <SiteHeader />
      <PageHero crumb="Services" eyebrow={`Service ${String(idx + 1).padStart(2, "0")}`} title={service.name} description={service.text} />
      {service.img && (
        <div className="nx-wrap pt-12" data-testid="service-image-wrap">
          <Reveal>
            <div className="border border-white/10 overflow-hidden">
              <img src={service.img} alt={service.name} className="w-full h-64 sm:h-80 lg:h-96 object-cover" data-testid="service-image" loading="lazy" />
            </div>
          </Reveal>
        </div>
      )}
      <section className="py-16 sm:py-24">
        <div className="nx-wrap grid lg:grid-cols-[1.4fr_1fr] gap-14">
          <div>
            <Reveal>
              <h2 className="font-marcellus text-2xl sm:text-3xl text-white mb-8">What&apos;s included</h2>
            </Reveal>
            <Stagger className="flex flex-col gap-px bg-white/10 border border-white/10">
              {service.includes.map((d, i) => (
                <StaggerItem key={d}>
                  <div className="flex items-center gap-5 bg-nx-navy px-6 py-5" data-testid={`service-include-${i}`}>
                    <span className="flex items-center justify-center w-7 h-7 border border-nx-gold/40 shrink-0"><Check size={14} className="text-nx-goldlight" /></span>
                    <span className="text-sm sm:text-base text-slate-200">{d}</span>
                  </div>
                </StaggerItem>
              ))}
            </Stagger>
          </div>
          <div className="flex flex-col gap-8">
            <Reveal delay={0.1}>
              <div className="border border-nx-gold/40 p-7">
                <span className="text-[10px] tracking-[0.28em] uppercase text-slate-500">Ready to proceed?</span>
                <p className="text-sm text-slate-300 mt-3 mb-5 leading-relaxed">Pay for this service online through our secure Razorpay checkout.</p>
                <PayNow product={service.name} />
              </div>
            </Reveal>
            <Reveal delay={0.15}>
              <Link to={`/services/${next.slug}`} className="group block border border-white/10 hover:border-nx-gold/50 p-7 transition-colors" data-testid="service-next-link">
                <span className="text-[10px] tracking-[0.28em] uppercase text-slate-500">Next service</span>
                <span className="flex items-center justify-between mt-3">
                  <span className="font-marcellus text-xl text-white">{next.name}</span>
                  <ArrowRight size={18} className="text-nx-goldlight transition-transform duration-300 group-hover:translate-x-1" />
                </span>
              </Link>
            </Reveal>
            <Reveal delay={0.2}>
              <Link to="/services" className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-slate-400 hover:text-nx-goldlight transition-colors" data-testid="service-back-link">
                <ArrowLeft size={14} /> All services
              </Link>
            </Reveal>
            <Reveal delay={0.25}>
              <div className="border-t border-white/10 pt-6" data-testid="service-related">
                <span className="text-[10px] tracking-[0.28em] uppercase text-slate-500">Related services</span>
                <div className="mt-3 flex flex-col gap-2.5">
                  {SERVICES.filter((s) => s.slug !== slug).slice(0, 3).map((s) => (
                    <Link key={s.slug} to={`/services/${s.slug}`} className="text-xs text-slate-400 hover:text-nx-goldlight transition-colors" data-testid={`service-related-${s.slug}`}>
                      {s.name} →
                    </Link>
                  ))}
                  <Link to="/" className="text-xs text-slate-400 hover:text-nx-goldlight transition-colors" data-testid="service-home-link">NexSecure Technologies — Home →</Link>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
      {service.subs && (
        <section className="py-16 sm:py-24 bg-nx-navy2 border-t border-white/10" data-testid="service-subs">
          <div className="nx-wrap">
            <Reveal>
              <div className="flex items-center gap-4 mb-4">
                <span className="h-px w-12 bg-nx-gold/70" />
                <span className="text-[10px] sm:text-xs tracking-[0.16em] sm:tracking-[0.24em] uppercase text-nx-goldlight">What We Cover</span>
              </div>
              <h2 className="font-marcellus text-2xl sm:text-3xl text-white leading-tight max-w-2xl mb-12">Software services, end to end.</h2>
            </Reveal>
            <Stagger className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {service.subs.map((sub, i) => (
                <StaggerItem key={sub.name}>
                  <div className="flex flex-col h-full border border-white/10 bg-nx-navy p-7 hover:border-nx-gold/40 transition-colors duration-300" data-testid={`service-sub-${i}`}>
                    <h3 className="font-marcellus text-lg text-white leading-snug">{sub.name}</h3>
                    <p className="text-sm text-slate-400 mt-3 leading-relaxed flex-1">{sub.description}</p>
                    <Link to="/request-a-quote" className="group inline-flex items-center gap-2 mt-6 text-xs tracking-[0.16em] uppercase text-nx-goldlight hover:text-white transition-colors" data-testid={`service-sub-quote-${i}`}>
                      Get a Quote <ArrowRight size={13} className="transition-transform duration-300 group-hover:translate-x-1" />
                    </Link>
                  </div>
                </StaggerItem>
              ))}
            </Stagger>
          </div>
        </section>
      )}
      {service.showcase && (
        <section className="py-16 sm:py-24 border-t border-white/10" data-testid="service-showcase">
          <div className="nx-wrap">
            <Reveal>
              <div className="flex items-center gap-4 mb-4">
                <span className="h-px w-12 bg-nx-gold/70" />
                <span className="text-[10px] sm:text-xs tracking-[0.16em] sm:tracking-[0.24em] uppercase text-nx-goldlight">Software Portfolio</span>
              </div>
              <h2 className="font-marcellus text-2xl sm:text-3xl text-white leading-tight max-w-2xl mb-12">Software we build, run and trust ourselves.</h2>
            </Reveal>
            <Stagger className="grid sm:grid-cols-2 gap-6">
              {service.showcase.map((p, i) => (
                <StaggerItem key={p.name}>
                  <div className="flex flex-col h-full border border-white/10 bg-nx-navy2 p-7 hover:border-nx-gold/40 transition-colors duration-300" data-testid={`service-showcase-${i}`}>
                    <span className="text-[10px] tracking-[0.18em] uppercase text-nx-goldlight mb-3">{p.tag}</span>
                    <h3 className="font-marcellus text-lg text-white leading-snug">{p.name}</h3>
                    <p className="text-sm text-slate-400 mt-3 leading-relaxed flex-1">{p.text}</p>
                  </div>
                </StaggerItem>
              ))}
            </Stagger>
          </div>
        </section>
      )}
      <CTABand title={`Need ${service.name.toLowerCase()} for your site?`} />
      <SiteFooter />
    </div>
  );
}
